// services/progressService.js
import { apiFetch } from "./apiFetch";

const API_BASE_URL = process.env.REACT_APP_BACKEND_API_URL || 'http://localhost:8000/api';
const statusCache = new Map();

/**
 * Progress Service
 * Handles all API calls related to user progress tracking
 */
export const progressService = {
  /**
   * Get user progress for a specific subsection
   * @param {string} userId - User UUID
   * @param {string} subsectionId - Subsection UUID
   * @returns {Promise<Object>} Progress data
   */
  async getUserProgress(userId, subsectionId) {
    try {
      const response = await apiFetch(
        `${API_BASE_URL}/users/${userId}/progress/${subsectionId}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.status === 404) {
        console.log('No progress found for this subsection');
        return null;
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data?.status) {
        statusCache.set(subsectionId, data.status);
      }
      console.log('Progress loaded:', data);
      return data;
    } catch (error) {
      console.error('Error fetching user progress:', error);
      throw error;
    }
  },

  /**
   * Create or update user progress
   * @param {string} userId - User UUID
   * @param {string} subsectionId - Subsection UUID
   * @param {Object} data - Form data to save
   * @param {string} status - Progress status (in_progress | completed)
   * @returns {Promise<Object>} Updated progress data
   */
  async saveProgress(userId, subsectionId, data, status = 'in_progress') {
    try {
      const cachedStatus = statusCache.get(subsectionId);
      const resolvedStatus =
        cachedStatus === 'completed' && status !== 'completed'
          ? 'completed'
          : status;

      console.log('Saving progress:', {
        userId,
        subsectionId,
        status: resolvedStatus,
        data,
      });

      const response = await apiFetch(
        `${API_BASE_URL}/users/${userId}/progress/${subsectionId}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            status: resolvedStatus,
            data: data,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `Failed to save progress: ${response.status}`);
      }

      const result = await response.json();
      if (result?.status) {
        statusCache.set(subsectionId, result.status);
      }
      console.log('Progress saved successfully:', result);
      return result;
    } catch (error) {
      console.error('Error saving progress:', error);
      throw error;
    }
  },

  /**
   * Merge with existing progress data before saving.
   */
  async saveMergedProgress(userId, subsectionId, data, status = 'in_progress') {
    try {
      const existing = await this.getUserProgress(userId, subsectionId).catch(() => null);
      const existingData = existing?.data ?? {};
      const mergedData = {
        ...existingData,
        ...data,
      };
      return await this.saveProgress(userId, subsectionId, mergedData, status);
    } catch (error) {
      console.error('Error saving merged progress:', error);
      throw error;
    }
  },

  /**
   * Update user state (current position)
   * @param {string} userId - User UUID
   * @param {string} currentSubsectionId - Current subsection UUID
   * @param {string|null} recommendedNext - Next recommended subsection UUID
   * @returns {Promise<Object>} Updated user state
   */
  async updateUserState(userId, currentSubsectionId, recommendedNext = null) {
    if (!userId || !currentSubsectionId) {
      console.warn('Skipping user state update: missing identifiers');
      return null;
    }

    try {
      console.log('Updating user state:', { userId, currentSubsectionId, recommendedNext });

      const response = await apiFetch(
        `${API_BASE_URL}/users/${userId}/state`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            current_subsection: currentSubsectionId,
            recommended_next: recommendedNext,
          }),
        }
      );

      if (!response.ok) {
        console.error(`Failed to update user state: ${response.status}`);
        return null;
      }

      const result = await response.json();
      console.log('User state updated:', result);
      return result;
    } catch (error) {
      console.error('Error updating user state:', error);
      return null;
    }
  },

  /**
   * Get course progress
   * @param {string} userId - User UUID
   * @param {string} courseId - Course UUID
   * @returns {Promise<Object>} Course progress with modules
   */
  async getCourseProgress(userId, courseId) {
    try {
      const response = await apiFetch(
        `${API_BASE_URL}/users/${userId}/courses/${courseId}/progress`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch course progress: ${response.status}`);
      }

      const data = await response.json();
      console.log('Course progress loaded:', data);
      return data;
    } catch (error) {
      console.error('Error fetching course progress:', error);
      throw error;
    }
  },

  /**
   * Get all user progress items
   * @param {string} userId - User UUID
   * @returns {Promise<Array>} Array of progress items
   */
  async getAllUserProgress(userId) {
    try {
      const response = await apiFetch(
        `${API_BASE_URL}/users/${userId}/progress`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch user progress: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching all user progress:', error);
      throw error;
    }
  },

  /**
   * Get user dashboard data
   * @param {string} userId - User UUID
   * @returns {Promise<Array>} Dashboard data with all courses
   */
  async getUserDashboard(userId) {
    try {
      const response = await apiFetch(
        `${API_BASE_URL}/users/${userId}/dashboard`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch dashboard data: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching dashboard:', error);
      throw error;
    }
  },


  async getUserDataForSection(userId, courseSlug, moduleSlug, subsectionSlug) {
  try {
    const response = await apiFetch(
      `${API_BASE_URL}/users/${userId}/data/section?course=${courseSlug}&module=${moduleSlug}&subsection=${subsectionSlug}`,
      {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      }
    );

    if (response.status === 404) {
      console.log('No progress data found');
      return null;
    }

    if (!response.ok)
      throw new Error(`HTTP error! status: ${response.status}`);

    const result = await response.json();
    return result.data;
  } catch (error) {
    console.error('Error fetching user data:', error);
    throw error;
  }
}

};

export default progressService;
